import React, { useEffect, useState } from 'react';
import api from '../services/api';
import toast from 'react-hot-toast';
import { CheckCircle2, Lock, PlayCircle } from 'lucide-react';
import { datumUndZeit } from '../lib/zeit';

// Einmaliger Lern-Cutover: Der Klick setzt keine Saat, sondern sperrt den zuvor im Markt-
// Cockpit belegten T-Startprofil-Snapshot. Danach ersetzen nur neue eigene VOOGO-Fahrten ihn.
export default function GoLiveStart() {
  const [status, setStatus] = useState({ loading: true });
  const [step, setStep] = useState('idle');
  const [password, setPassword] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const loadStatus = () => {
    // Der Schnitt ist irreversibel: Solange der Serverzustand nicht ausdrücklich
    // `started: false` bestätigt, darf die Oberfläche keine Startaktion anbieten.
    setStatus({ loading: true });
    return api.get('/admin/go-live/status')
      .then((response) => {
        const data = response && response.data;
        setStatus(data && typeof data.started === 'boolean'
          ? data
          : { unavailable: true });
      })
      .catch(() => setStatus({ unavailable: true }));
  };
  useEffect(() => { loadStatus(); }, []);

  const cancel = () => {
    setStep('idle');
    setPassword('');
    setReason('');
  };

  const start = async () => {
    const revision = status?.start_profile?.revision;
    if (status?.started !== false || status?.can_start !== true
        || !status?.start_profile?.ready || !/^[a-f0-9]{64}$/.test(String(revision || ''))
        || status?.start_profile_matches_upload_preview !== true
        || status?.loading || status?.unavailable) {
      toast.error('Go-Live-Status ist nicht sicher bestätigt. Bitte Status erneut prüfen.');
      return;
    }
    if (!password) { toast.error('Bitte Admin-Passwort eingeben.'); return; }
    if (reason.trim().length < 10) { toast.error('Bitte den Go-Live-Grund angeben.'); return; }
    setBusy(true);
    try {
      await api.post('/admin/go-live', {
        confirm: 'GO-LIVE-START',
        password,
        reason: reason.trim(),
        expected_start_profile_revision: revision,
      });
      toast.success('Go-Live gestartet. Das belegte Startprofil ist jetzt gesperrt.');
      cancel();
      loadStatus();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Go-Live konnte nicht gestartet werden.');
    } finally {
      setBusy(false);
    }
  };

  const GREEN = '#15803d';
  const card = {
    background: '#fff', border: '1px solid #d1d5db', borderRadius: 14,
    padding: 24, maxWidth: 560,
  };

  if (status?.loading) {
    return (
      <div style={card} role="status" aria-live="polite">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
          <Lock size={20} color="#4b5563" />
          <h2 style={{ fontSize: 16, fontWeight: 800, color: '#0a0a0a', margin: 0 }}>
            Go-Live-Status wird geprüft
          </h2>
        </div>
        <p style={{ color: '#4b5563', fontSize: 13, lineHeight: 1.6, marginBottom: 0 }}>
          Die Startaktion bleibt gesperrt, bis der Server den aktuellen Zustand bestätigt hat.
        </p>
      </div>
    );
  }

  if (status?.unavailable) {
    return (
      <div style={{ ...card, border: '1px solid #f59e0b' }} role="alert">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
          <Lock size={20} color="#92400e" />
          <h2 style={{ fontSize: 16, fontWeight: 800, color: '#0a0a0a', margin: 0 }}>
            Go-Live-Status nicht verfügbar
          </h2>
        </div>
        <p style={{ color: '#4b5563', fontSize: 13, lineHeight: 1.6, marginBottom: 14 }}>
          Der Status konnte nicht sicher geladen werden. Aus Sicherheitsgründen bleibt der
          irreversible Start gesperrt.
        </p>
        <button type="button" onClick={loadStatus}
          style={{ background: '#fff', color: '#92400e', border: '1px solid #f59e0b', borderRadius: 8, padding: '9px 14px', fontWeight: 700, cursor: 'pointer' }}>
          Status erneut prüfen
        </button>
      </div>
    );
  }

  if (status?.started) {
    const profile = status.start_profile || {};
    const target = Number(profile.target_observations);
    const credited = Number(profile.credited_observations);
    const progress = Number.isFinite(target) && target > 0 && Number.isFinite(credited)
      ? Math.min(100, Math.round((credited / target) * 100)) : null;
    return (
      <div style={{ ...card, border: `1px solid ${GREEN}55` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
          <CheckCircle2 size={20} color={GREEN} />
          <h2 style={{ fontSize: 16, fontWeight: 800, color: '#0a0a0a', margin: 0 }}>Go-Live aktiv</h2>
        </div>
        <p style={{ color: '#4b5563', fontSize: 13, lineHeight: 1.6, marginBottom: 0 }}>
          Gestartet{status.started_at ? ` am ${datumUndZeit(status.started_at, { jahr: 'voll', sekunden: true })}` : ''}.
          Ausschließlich eigene Ultimate-Fahrten, die ab diesem Zeitpunkt angelegt werden,
          dürfen in das Lernen einfließen. Frühere Fahrten bleiben dauerhaft ausgeschlossen.
          Das zuvor gesetzte Startprofil ist gesperrt; spätere Uploads verändern es nicht.
          {progress === null
            ? ' Der Ersetzungsfortschritt ist derzeit nicht sicher messbar.'
            : ` Eigene Fahrten haben bislang ${progress} % des 10-%-Ersetzungshorizonts erreicht.`}
          Marktpreise, Preisparameter, Konten, Fahrten und Finanzdaten wurden nicht verändert.
        </p>
      </div>
    );
  }

  if (status?.can_start !== true || !status?.start_profile?.ready
      || status?.start_profile_matches_upload_preview !== true) {
    return (
      <div style={{ ...card, border: '1px solid #f59e0b' }} role="alert">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
          <Lock size={20} color="#92400e" />
          <h2 style={{ fontSize: 16, fontWeight: 800, color: '#0a0a0a', margin: 0 }}>
            Go-Live noch gesperrt
          </h2>
        </div>
        <p style={{ color: '#4b5563', fontSize: 13, lineHeight: 1.6, marginBottom: 14 }}>
          Bitte zuerst den aktuellen Uploadstand unter Markt &amp; Konkurrenz → Normal-Nachfrage
          als Startprofil setzen. Wenn danach eine neue Upload-Woche hinzukommt, muss der
          aktualisierte Stand erneut gesetzt werden.
        </p>
        <a href="/agent?tab=konkurrenz&markt=normale"
          style={{ color: '#92400e', fontWeight: 700, fontSize: 13 }}>
          Zur Normal-Nachfrage
        </a>
      </div>
    );
  }

  return (
    <div style={card}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
        <PlayCircle size={20} color="#111827" />
        <h2 style={{ fontSize: 16, fontWeight: 800, color: '#0a0a0a', margin: 0 }}>Go-Live starten</h2>
      </div>
      <p style={{ color: '#4b5563', fontSize: 13, lineHeight: 1.6, marginBottom: 16 }}>
        Der Betrieb läuft bereits vollständig über denselben Preis-, Zahlungs- und Abrechnungspfad.
        Dieser irreversible Schritt setzt den Lern-Stichtag für <b>zukünftig angelegte eigene
        Ultimate-Fahrten</b> und sperrt das zuvor gesetzte Startprofil dauerhaft. Er setzt beim
        Go-Live keine neue Saat und übernimmt keine neuen Uploaddaten. Spätere Uploads verändern T
        nicht; sie bleiben Marktanzeige. Tarif, Fahrpreis, Geldpfad und Bestandsdaten werden durch
        diesen Klick nicht zurückgesetzt oder umgebucht.
      </p>

      {step === 'idle' ? (
        <button onClick={() => setStep('confirm')}
          style={{ background: '#111827', color: '#fff', border: 'none', borderRadius: 10, padding: '10px 16px', fontWeight: 700, cursor: 'pointer', fontSize: 14 }}>
          Go-Live vorbereiten …
        </button>
      ) : (
        <div style={{ background: '#f9fafb', border: '1px solid #d1d5db', borderRadius: 12, padding: 16 }}>
          <p style={{ color: '#111827', fontSize: 13, fontWeight: 700, marginTop: 0, marginBottom: 12 }}>
            <Lock size={13} style={{ verticalAlign: -2, marginRight: 6 }} />
            Dieser Lern-Cutover ist dauerhaft. Er sperrt das belegte Startprofil; danach ersetzt
            ausschließlich eigene VOOGO-Evidenz jede Zelle. Er ist kein Preis- oder Datenreset.
            Bitte Entscheidung begründen und mit deinem Admin-Passwort bestätigen.
          </p>
          <input value={reason} onChange={(event) => setReason(event.target.value)}
            placeholder="Grund für den Go-Live" maxLength={300}
            style={{ width: '100%', boxSizing: 'border-box', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, marginBottom: 10 }} />
          <input type="password" value={password} onChange={(event) => setPassword(event.target.value)}
            autoComplete="off" placeholder="Admin-Passwort"
            onKeyDown={(event) => { if (event.key === 'Enter' && !busy) start(); }}
            style={{ width: '100%', boxSizing: 'border-box', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, marginBottom: 12 }} />
          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={cancel} disabled={busy}
              style={{ background: '#fff', color: '#374151', border: '1px solid #d1d5db', borderRadius: 8, padding: '9px 14px', fontWeight: 600, cursor: 'pointer' }}>
              Abbrechen
            </button>
            <button onClick={start} disabled={busy || !password || reason.trim().length < 10}
              style={{ background: (busy || !password || reason.trim().length < 10) ? '#6b7280' : GREEN, color: '#fff', border: 'none', borderRadius: 8, padding: '9px 14px', fontWeight: 700, cursor: busy ? 'wait' : 'pointer' }}>
              {busy ? 'Wird gestartet …' : 'Go-Live endgültig starten'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
